/**
 * 보육 계획안·일지 양식 (§5.14, v1.19) — 단일 원천.
 *
 * 현장에서 실제로 조금씩 바꿔 쓰는 다섯 서식을 앱 양식으로 재구성했다:
 * 연간교육계획안 · 월간 놀이 계획안 · 주간 놀이 계획안 · 주간 놀이 일지 · 일일 놀이 일지.
 * (2019 개정 누리과정의 유아·놀이중심 기록 원칙 위에서, 원별 변형이 쉬운 구조로)
 * 화면 편집기·AI 초안·양식 자료실 PDF가 모두 이 정의를 쓴다.
 */
export const JOURNAL_DAYS = [
    { key: 'mon', label: '월' },
    { key: 'tue', label: '화' },
    { key: 'wed', label: '수' },
    { key: 'thu', label: '목' },
    { key: 'fri', label: '금' },
];
export const JOURNAL_WEEKS = [
    { key: 'w1', label: '1주' },
    { key: 'w2', label: '2주' },
    { key: 'w3', label: '3주' },
    { key: 'w4', label: '4주' },
    { key: 'w5', label: '5주' },
];
/** 학년도 순서(3월 시작) — 연간계획안의 열 순서와 같다 */
export const JOURNAL_MONTHS = [
    { key: 'm03', label: '3월' },
    { key: 'm04', label: '4월' },
    { key: 'm05', label: '5월' },
    { key: 'm06', label: '6월' },
    { key: 'm07', label: '7월' },
    { key: 'm08', label: '8월' },
    { key: 'm09', label: '9월' },
    { key: 'm10', label: '10월' },
    { key: 'm11', label: '11월' },
    { key: 'm12', label: '12월' },
    { key: 'm01', label: '1월' },
    { key: 'm02', label: '2월' },
];
export function journalUnits(per) {
    if (per === 'day')
        return JOURNAL_DAYS;
    if (per === 'week')
        return JOURNAL_WEEKS;
    return JOURNAL_MONTHS;
}
export const JOURNAL_TEMPLATES = [
    {
        code: 'ANNUAL_PLAN',
        span: 'YEAR',
        name: '연간 계획안',
        summary: '한 해(3월~2월)의 월별 주제와 기대되는 놀이를 담는 계획안',
        origin: '현장 연간교육계획안 서식 재구성 — 월별 주제·기대되는 놀이·놀이 키워드',
        sections: [
            {
                title: '월별 계획 (3월~2월)',
                fields: [
                    { key: 'monthTheme', label: '예상 주제', per: 'month', rows: 1 },
                    {
                        key: 'monthPlays',
                        label: '기대되는 놀이',
                        hint: '한 줄에 하나씩 — 유아에게서 발현되는 놀이가 반영될 여지를 두고',
                        per: 'month',
                        rows: 3,
                    },
                    {
                        key: 'monthKeywords',
                        label: '놀이 키워드',
                        hint: '#알록달록 #봄 처럼 키워드로',
                        per: 'month',
                        rows: 1,
                    },
                ],
            },
        ],
    },
    {
        code: 'MONTHLY_PLAN',
        span: 'MONTH',
        name: '월간 계획안',
        summary: '이달의 주제와 주차별 놀이 흐름을 담는 계획안',
        origin: '현장 월간 놀이 계획안 서식 재구성 — 주제·선정 이유·주차별 놀이',
        sections: [
            {
                title: '이달의 주제',
                fields: [
                    { key: 'theme', label: '놀이 주제', rows: 1 },
                    { key: 'expectation', label: '교사의 기대', rows: 2 },
                    {
                        key: 'reason',
                        label: '주제 선정 이유',
                        hint: '주차 구성 의도를 함께 — 유아의 관심에서 출발했는지',
                        rows: 3,
                    },
                ],
            },
            {
                title: '주차별 놀이 계획',
                fields: [
                    {
                        key: 'weekPlay',
                        label: '예상 놀이·놀이',
                        hint: '그 주의 예상 놀이와 놀이 흐름을 한 줄에 하나씩',
                        per: 'week',
                        rows: 4,
                    },
                    { key: 'weekOutdoor', label: '바깥놀이(대체)', per: 'week', rows: 2 },
                    { key: 'weekSafety', label: '안전교육·기본생활습관', per: 'week', rows: 2 },
                ],
            },
            {
                title: '한 달 공통 일과',
                fields: [
                    { key: 'arrival', label: '등원 및 맞이하기', rows: 1 },
                    { key: 'dailyLife', label: '일상생활(점심·낮잠·배변활동)', rows: 2 },
                    { key: 'homeConnection', label: '가정 연계', rows: 1 },
                ],
            },
        ],
    },
    {
        code: 'WEEKLY_PLAN',
        span: 'WEEK',
        name: '주간 계획안',
        summary: '이번 주 주제와 놀이·일상·안전교육을 담는 계획안',
        origin: '현장 주간 놀이 계획안 서식 재구성 — 주제·놀이·일상생활·안전교육·가정 연계',
        sections: [
            {
                title: '이번 주 주제',
                fields: [
                    { key: 'theme', label: '놀이 주제', rows: 1 },
                    { key: 'expectedPlay', label: '예상 놀이', rows: 1 },
                    { key: 'expectation', label: '교사의 기대', rows: 2 },
                ],
            },
            {
                title: '놀이 계획',
                fields: [
                    {
                        key: 'play',
                        label: '놀이',
                        hint: '한 줄에 하나씩 — 우발적으로 일어나는 놀이가 들어올 여지를 두고',
                        rows: 5,
                    },
                    { key: 'outdoorPlay', label: '바깥놀이(실내대체)', rows: 2 },
                ],
            },
            {
                title: '일상·기본생활',
                fields: [
                    { key: 'arrival', label: '등원 및 맞이하기', rows: 1 },
                    { key: 'lunchSnack', label: '점심 및 간식', rows: 1 },
                    { key: 'nap', label: '낮잠', rows: 1 },
                    { key: 'toileting', label: '배변활동', rows: 1 },
                    { key: 'basicHabits', label: '기본생활습관', rows: 1 },
                ],
            },
            {
                title: '안전·가정',
                fields: [
                    { key: 'safety', label: '안전교육', hint: '요일별 계획이 있으면 나눠서', per: 'day', rows: 2 },
                    { key: 'homeConnection', label: '가정 연계', rows: 1 },
                ],
            },
        ],
    },
    {
        code: 'WEEKLY_JOURNAL',
        span: 'WEEK',
        name: '주간 일지',
        summary: '한 주의 일과 실행 기록과 놀이 지원·배움 읽기',
        origin: '현장 주간 놀이 일지 서식 재구성 — 일과 실행 기록·놀이 지원과 배움 읽기',
        ai: true,
        sections: [
            {
                title: '이번 주',
                fields: [
                    { key: 'theme', label: '놀이 주제', rows: 1 },
                    { key: 'expectedPlay', label: '예상 놀이', rows: 1 },
                    { key: 'expectation', label: '교사의 기대', rows: 2 },
                ],
            },
            {
                title: '일과 실행 기록',
                fields: [
                    { key: 'arrival', label: '등원 및 맞이하기', rows: 2 },
                    { key: 'morningCare', label: '오전 간식·화장실 가기', rows: 2 },
                    {
                        key: 'play',
                        label: '놀이',
                        hint: '놀이별 실행 모습과 교사의 지원을 함께',
                        rows: 6,
                    },
                    { key: 'outdoor', label: '바깥놀이(실내대체)·손 씻기', rows: 2 },
                    { key: 'lunch', label: '점심 식사', rows: 1 },
                    { key: 'nap', label: '낮잠', rows: 1 },
                    { key: 'afternoon', label: '오후 간식·오후 놀이', rows: 2 },
                    { key: 'closing', label: '하원 및 통합보육', rows: 1 },
                ],
            },
            {
                title: '놀이 지원과 배움 읽기',
                fields: [
                    {
                        key: 'dayReading',
                        label: '요일별 배움 읽기',
                        hint: '그날의 놀이 관찰, 교사의 지원, 다음 놀이로 이어 갈 계획',
                        per: 'day',
                        rows: 3,
                    },
                ],
            },
            {
                title: '비고',
                fields: [{ key: 'note', label: '비고', hint: '안전교육 실시 내역 등', rows: 2 }],
            },
        ],
    },
    {
        code: 'DAILY_JOURNAL',
        span: 'DAY',
        name: '일일 일지',
        summary: '하루의 준비·일과 실행·놀이 평가를 담는 일지',
        origin: '현장 일일 놀이 일지 서식 재구성 — 환경점검·일일시간표·일과 실행·놀이 평가',
        ai: true,
        sections: [
            {
                title: '오늘',
                fields: [
                    { key: 'theme', label: '놀이 주제', rows: 1 },
                    { key: 'expectedPlay', label: '예상 놀이', rows: 1 },
                    { key: 'expectation', label: '교사의 기대', rows: 2 },
                ],
            },
            {
                title: '준비·환경점검',
                fields: [
                    {
                        key: 'prep',
                        label: '교사의 준비 및 환경점검',
                        hint: '환기·청결·놀잇감 점검, 소지품 확인 등',
                        rows: 2,
                    },
                    {
                        key: 'timetable',
                        label: '일일시간표',
                        hint: '07:30 등원 및 맞이하기 / 09:30 놀이 … 처럼 시간대로',
                        rows: 3,
                    },
                ],
            },
            {
                title: '일과 실행',
                fields: [
                    { key: 'arrival', label: '등원 및 맞이하기', rows: 2 },
                    { key: 'morningCare', label: '오전 간식·화장실 가기', rows: 2 },
                    {
                        key: 'play',
                        label: '놀이',
                        hint: '활동명과 실행 모습, 교사의 지원을 함께',
                        rows: 6,
                    },
                    { key: 'outdoor', label: '바깥놀이(실내대체)·손 씻기', rows: 2 },
                    { key: 'lunch', label: '점심 식사 및 이 닦기', rows: 1 },
                    { key: 'nap', label: '낮잠', rows: 1 },
                    { key: 'afternoon', label: '오후 간식·오후 놀이', rows: 2 },
                    { key: 'closing', label: '하원 및 통합보육', rows: 1 },
                ],
            },
            {
                title: '평가',
                fields: [
                    { key: 'materials', label: '유의점 및 준비물', rows: 2 },
                    {
                        key: 'evaluation',
                        label: '놀이 평가',
                        hint: '놀이에 대한 평가와 다음날 지원 계획',
                        rows: 4,
                    },
                ],
            },
        ],
    },
];
export function findJournalTemplate(code) {
    return JOURNAL_TEMPLATES.find((t) => t.code === code);
}
/** 양식 자료실 카피 (v1.19) */
export const FORMS_COPY = {
    title: '양식 자료실',
    intro: '현장에서 실제 쓰는 계획안·일지 서식을 우리 스타일로 다시 그린 양식이에요. 인쇄해서 쓰거나, 보육일지 화면에서 같은 양식으로 바로 작성할 수 있어요.',
    download: 'PDF 받기',
    writeInApp: '앱에서 작성',
};
export const CARE_JOURNAL_COPY = {
    title: '보육일지',
    aiDraft: 'AI 초안 만들기',
    aiNotice: '이 반의 관찰메모만 근거로 초안을 만들어요. 관찰하지 않은 이야기는 지어내지 않고, 저장 전에 선생님이 확인해요.',
    noObservations: '이 기간의 관찰메모가 아직 없어요. 직접 입력하거나 관찰을 남긴 뒤 다시 시도해 주세요.',
    templatePick: '양식 고르기',
    approved: '원장 확인 완료',
};
//# sourceMappingURL=care-journal.js.map